/**
 * Cookie consent banner — GDPR / CCPA compliant.
 *
 * Shows once per browser. Records the user's choice in localStorage so we
 * never nag them again. Three buttons:
 *   • Accept all  → consent.analytics = true
 *   • Reject all  → consent.analytics = false (only strictly-necessary cookies)
 *   • Manage      → expands to show category toggles
 *
 * Why a custom-built one instead of OneTrust / Cookiebot:
 *   Those services cost $300+/mo and add 80KB of JS. We don't currently
 *   load analytics or marketing cookies, so this is mostly a trust signal
 *   + legal cover. If we ever add GA4 / Meta Pixel, gate them on
 *   `getCookieConsent().analytics === true` before initializing.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, X, Cookie } from 'lucide-react';

const STORAGE_KEY = 'qs-cookie-consent-v1';

export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const persist = (consent) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...consent,
      acceptedAt: new Date().toISOString(),
    }));
  } catch { /* localStorage full / disabled */ }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    // Defer 1.5s so the banner doesn't slam in during initial paint.
    const t = setTimeout(() => {
      if (!getCookieConsent()) setVisible(true);
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  const acceptAll = () => {
    persist({ essential: true, analytics: true });
    setVisible(false);
  };
  const rejectAll = () => {
    persist({ essential: true, analytics: false });
    setVisible(false);
  };
  const saveCustom = () => {
    persist({ essential: true, analytics });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[60] animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="bg-white rounded-2xl shadow-2xl border-2 border-slate-200 p-5 relative">
        <button
          type="button"
          onClick={rejectAll}
          aria-label="Close (reject non-essential)"
          className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-700 rounded"
          data-testid="cookie-banner-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-blue-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-slate-900 text-sm pr-6">
              We respect your privacy
            </h2>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              We use only strictly-necessary cookies to keep you logged in.
              No tracking, no ad networks, no Facebook Pixel. Optional analytics
              help us improve the product.{' '}
              <Link
                to="/privacy"
                className="text-blue-700 underline hover:text-blue-800"
                data-testid="cookie-banner-policy-link"
              >
                Privacy Policy
              </Link>
              .
            </p>

            {expanded && (
              <div className="mt-3 space-y-2 text-xs" data-testid="cookie-banner-categories">
                <div className="flex items-start gap-2 p-2 rounded-md bg-slate-50">
                  <input
                    type="checkbox"
                    checked
                    disabled
                    className="mt-0.5 accent-blue-600"
                    aria-label="Essential cookies (always on)"
                  />
                  <div>
                    <strong className="text-slate-800">Strictly necessary</strong>
                    <p className="text-slate-600">Auth session, security tokens. Always on.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 rounded-md bg-slate-50">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-0.5 accent-blue-600"
                    aria-label="Analytics cookies"
                    data-testid="cookie-banner-analytics-toggle"
                  />
                  <div>
                    <strong className="text-slate-800">Analytics</strong>
                    <p className="text-slate-600">Anonymous usage stats (Sentry only — error tracking).</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                onClick={acceptAll}
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md px-3 py-2"
                data-testid="cookie-banner-accept-all"
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Accept all
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-md px-3 py-2"
                data-testid="cookie-banner-reject-all"
              >
                Reject non-essential
              </button>
              {!expanded ? (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="text-xs text-slate-600 hover:text-slate-900 underline self-center"
                  data-testid="cookie-banner-manage"
                >
                  Manage
                </button>
              ) : (
                <button
                  type="button"
                  onClick={saveCustom}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md px-3 py-2"
                  data-testid="cookie-banner-save-custom"
                >
                  Save preferences
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
